import WebApp from '@twa-dev/sdk';
import type { WebAppUser } from '@twa-dev/types';
import { themeBg } from '../design/tokens';
import type { Theme } from './theme';

/**
 * Обёртка над @twa-dev/sdk — одно место для инициализации Mini App.
 * Вызывается один раз в main.tsx.
 */
export function initTma(): void {
  // Сообщаем Telegram, что UI готов (скрывает splash).
  WebApp.ready();
  // Раскрываем окно на полный высоту.
  WebApp.expand();
  // Подтверждение при закрытии TMA — родной диалог Telegram
  // «Изменения могут не сохраниться». Включаем глобально.
  setClosingConfirmation(true);
  // Colours are ours (styles/index.css); the theme is applied by hooks/useTheme.ts.
}

export function getInitData(): string {
  return WebApp.initData ?? '';
}

export function getTmaUser(): WebAppUser | null {
  return WebApp.initDataUnsafe?.user ?? null;
}

export function isTmaEnv(): boolean {
  return Boolean(WebApp.initData && WebApp.initData.length > 0);
}

/**
 * Показывает Telegram BackButton и регистрирует обработчик.
 * Возвращает cleanup-функцию: снимает обработчик и скрывает кнопку.
 *
 * Используй так:
 *   useEffect(() => setBackButton(() => navigate(-1)), [navigate]);
 */
export function setBackButton(handler: () => void): () => void {
  WebApp.BackButton.onClick(handler);
  WebApp.BackButton.show();
  return () => {
    WebApp.BackButton.offClick(handler);
    WebApp.BackButton.hide();
  };
}

export function hideBackButton(): void {
  WebApp.BackButton.hide();
}

/**
 * Включает родной диалог Telegram «Изменения могут не сохраниться» при попытке
 * закрыть Mini App (свайп вниз / крестик). Текст задан Telegram'ом, кастомизации нет.
 *
 * Использование (включить, пока пользователь на странице с несохранёнными данными):
 *   useEffect(() => {
 *     setClosingConfirmation(true);
 *     return () => setClosingConfirmation(false);
 *   }, []);
 */
export function setClosingConfirmation(enabled: boolean): void {
  try {
    if (enabled) WebApp.enableClosingConfirmation();
    else WebApp.disableClosingConfirmation();
  } catch {
    // Вне Telegram — тихо игнорируем.
  }
}

/**
 * Тактильная отдача через Telegram. Безопасно вызывать вне TMA-окружения —
 * SDK сам no-op'ит, если HapticFeedback недоступен.
 *
 * Использование:
 *   haptic('light')   — обычные тапы (первичная кнопка)
 *   haptic('medium')  — переход на новый шаг, успешный submit
 *   haptic('success') — успешная генерация / оплата
 *   haptic('warning') — warning-тосты, paywall
 *   haptic('error')   — ошибка, destructive подтверждение
 */
export type HapticKind = 'light' | 'medium' | 'heavy' | 'success' | 'warning' | 'error';

export function haptic(kind: HapticKind): void {
  try {
    const fb = WebApp.HapticFeedback;
    if (!fb) return;
    if (kind === 'success' || kind === 'warning' || kind === 'error') {
      fb.notificationOccurred(kind);
    } else {
      fb.impactOccurred(kind);
    }
  } catch {
    // SDK может отсутствовать в браузере вне Telegram — тихо игнорируем.
  }
}

/** Opens a page in Telegram's in-app browser (falls back to a new tab outside Telegram). */
export function openExternalLink(url: string): void {
  try {
    WebApp.openLink(url);
  } catch {
    window.open(url, '_blank', 'noopener');
  }
}

/** Saves a file through Telegram (Bot API 8.0+); an older client opens the link instead. */
export function downloadFile(url: string, fileName: string): void {
  try {
    if (WebApp.isVersionAtLeast('8.0') && typeof WebApp.downloadFile === 'function') {
      WebApp.downloadFile({ url, file_name: fileName });
      return;
    }
  } catch {
    // An old client throws on unknown methods: fall through to the link.
  }
  openExternalLink(url);
}

/** Opens a t.me link inside Telegram, e.g. the bot chat (a new tab outside Telegram). */
export function openTelegramLink(url: string): void {
  try {
    WebApp.openTelegramLink(url);
  } catch {
    window.open(url, '_blank', 'noopener');
  }
}

/** Copies text: the Clipboard API, else a hidden textarea and execCommand. False when neither worked. */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Not allowed here (e.g. an old WebView): fall back below.
  }
  try {
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand('copy');
    area.remove();
    return ok;
  } catch {
    return false;
  }
}

/**
 * Asks Telegram for the person's own number (Bot API 6.9+). Null on refusal, an old client
 * or outside Telegram; the bot gets a contact message, which it leaves unanswered.
 */
export function requestPhone(): Promise<string | null> {
  return new Promise((resolve) => {
    try {
      if (!WebApp.isVersionAtLeast('6.9')) return resolve(null);
      WebApp.requestContact((ok, response) => {
        const phone = ok && response?.status === 'sent' ? response.responseUnsafe?.contact?.phone_number : undefined;
        resolve(phone ?? null);
      });
    } catch {
      resolve(null);
    }
  });
}

/** Telegram's own payment window for a Stars invoice link (Bot API 6.1+). */
export function openInvoice(url: string): Promise<'paid' | 'cancelled' | 'failed' | 'pending'> {
  return new Promise((resolve) => {
    try {
      WebApp.openInvoice(url, (status) => resolve(status));
    } catch {
      resolve('failed');
    }
  });
}

/* === Theme === */

/** Telegram's colour scheme; light outside Telegram or on an error. */
export function telegramScheme(): Theme {
  try {
    return WebApp.colorScheme === 'dark' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

/** Switches the page to a theme and paints Telegram's own bars in its background colour. */
export function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  root.classList.toggle('dark', theme === 'dark');
  const bg = themeBg(theme) as `#${string}`;
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', bg);
  try {
    WebApp.setHeaderColor(bg);
  } catch {
    // Outside Telegram or an old client: nothing to paint.
  }
  try {
    WebApp.setBackgroundColor(bg);
  } catch {
    // Outside Telegram or an old client: nothing to paint.
  }
  try {
    if (WebApp.isVersionAtLeast('7.10')) WebApp.setBottomBarColor(bg);
  } catch {
    // Outside Telegram or an old client: nothing to paint.
  }
}
